import React from 'react';
import { Table, Button, Space, Tag, Popconfirm, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { getCategoryDisplayName } from '../../../hooks/useUnits';

/**
 * UnitsTable Component
 * ตารางแสดงข้อมูลหน่วยนับพร้อม CRUD operations
 */
const UnitsTable = ({
  dataSource,
  loading,
  pagination,
  sorter,
  selectedRowKeys,
  onTableChange,
  onEdit,
  onDelete,
  onView,
  onSelectionChange,
  canEdit = true,
  canDelete = true
}) => {
  /**
   * คอลัมน์ของตาราง
   */
  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'display_order',
      key: 'display_order',
      width: 80,
      align: 'center',
      sorter: true,
      sortOrder: sorter.field === 'display_order' ? sorter.order : null,
      render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>
    },
    {
      title: 'ชื่อหน่วย',
      dataIndex: 'unit_name',
      key: 'unit_name',
      sorter: true,
      sortOrder: sorter.field === 'unit_name' ? sorter.order : null,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <Tooltip title={record.description || text}>
          <div>
            <div style={{ fontWeight: 500, color: '#1890ff' }}>
              {text}
            </div>
            {record.description && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
                {record.description}
              </div>
            )}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'รหัสหน่วย',
      dataIndex: 'unit_code',
      key: 'unit_code',
      width: 120,
      align: 'center',
      render: (text) => (
        <Tag color="blue" style={{ margin: 0 }}>
          {text || '-'}
        </Tag>
      )
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      filters: [
        { text: 'หน่วยนับ', value: 'count' },
        { text: 'น้ำหนัก', value: 'weight' },
        { text: 'ปริมาตร', value: 'volume' },
        { text: 'ความยาว', value: 'length' },
        { text: 'พื้นที่', value: 'area' },
        { text: 'เวลา', value: 'time' },
        { text: 'ภาชนะ', value: 'container' }
      ],
      render: (category) => {
        const categoryColors = {
          count: 'green',
          weight: 'orange', 
          volume: 'blue',
          length: 'purple',
          area: 'magenta',
          time: 'cyan',
          container: 'gold'
        };
        
        return (
          <Tag color={categoryColors[category] || 'default'}>
            {getCategoryDisplayName(category)}
          </Tag>
        );
      }
    },
    {
      title: 'สถานะ',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      filters: [
        { text: 'ใช้งาน', value: true },
        { text: 'ไม่ใช้งาน', value: false }
      ],
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </Tag>
      )
    },
    {
      title: 'สร้างเมื่อ',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      align: 'center',
      sorter: true,
      sortOrder: sorter.field === 'created_at' ? sorter.order : null,
      render: (dateString) => {
        if (!dateString) return '-';
        
        const date = new Date(dateString);
        return (
          <div style={{ fontSize: '12px' }}>
            <div>{date.toLocaleDateString('th-TH')}</div>
            <div style={{ color: '#666' }}>
              {date.toLocaleTimeString('th-TH', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        );
      }
    },
    {
      title: 'การจัดการ',
      key: 'actions',
      width: 140,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          {/* ปุ่มดู */}
          {onView && (
            <Tooltip title="ดูรายละเอียด">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => onView(record)}
                size="small"
              />
            </Tooltip>
          )}
          
          {/* ปุ่มแก้ไข */}
          {canEdit && (
            <Tooltip title="แก้ไข">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                size="small"
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}
          
          {/* ปุ่มลบ */}
          {canDelete && (
            <Popconfirm
              title="ยืนยันการลบ"
              description={
                <div>
                  <div>ต้องการลบหน่วยนับ <strong>"{record.unit_name}"</strong> ใช่หรือไม่?</div>
                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                    หากหน่วยนับนี้กำลังใช้งาน จะถูกปิดการใช้งานแทน
                  </div>
                </div>
              }
              onConfirm={() => onDelete(record.id)}
              okText="ลบ"
              cancelText="ยกเลิก"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="ลบ">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  /**
   * การเลือกแถว
   */
  const rowSelection = onSelectionChange ? {
    selectedRowKeys,
    onChange: onSelectionChange,
    getCheckboxProps: (record) => ({
      disabled: !record.is_active && !canDelete, // ปิดการเลือกถ้าไม่สามารถลบได้
    }),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: 'active',
        text: 'เลือกเฉพาะที่ใช้งาน',
        onSelect: (changeableRowKeys) => {
          const activeKeys = changeableRowKeys.filter(key => {
            const record = dataSource.find(item => item.id === key);
            return record && record.is_active;
          });
          onSelectionChange(activeKeys);
        },
      },
      {
        key: 'inactive',
        text: 'เลือกเฉพาะที่ไม่ใช้งาน',
        onSelect: (changeableRowKeys) => {
          const inactiveKeys = changeableRowKeys.filter(key => {
            const record = dataSource.find(item => item.id === key);
            return record && !record.is_active;
          });
          onSelectionChange(inactiveKeys);
        },
      }
    ]
  } : undefined;

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `แสดง ${range[0]}-${range[1]} จาก ${total} รายการ`
      }}
      rowKey="id"
      rowSelection={rowSelection}
      onChange={onTableChange}
      scroll={{ x: 1000 }}
      size="small"
      bordered
      style={{
        backgroundColor: '#fff',
        borderRadius: 8,
        overflow: 'hidden'
      }}
      rowClassName={(record, index) => 
        index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
      }
    />
  );
};

export default UnitsTable;